/**
 * 检测元素是否在视口中的工具函数
 * 优先使用 IntersectionObserver API（现代浏览器）
 * 在不支持的浏览器中回退到 getBoundingClientRect 方法
 */
import {throttle} from 'lodash-es';
import * as store from '@/store';

// 定义回调函数类型
type VisibilityCallback = (isVisible: boolean, entry?: IntersectionObserverEntry) => void;

// 定义配置选项类型
interface VisibilityOptions {
    root?: Element | Document | null;
    rootMargin?: string;
    threshold?: number | number[];
    once?: boolean;
}

// 缓存已观察的元素和对应的观察器
const elementObserverMap = new WeakMap<Element, IntersectionObserver>();

/**
 * 检测元素是否在视口中（使用 getBoundingClientRect 方法）
 * @param element 要检测的DOM元素
 * @param threshold 可见比例阈值（0-1）
 * @returns 元素是否在视口中
 */
function isElementInViewport(element: Element, threshold: number = 0): boolean {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    // 计算元素在视口中的可见比例
    const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
    const visibleWidth = Math.min(rect.right, windowWidth) - Math.max(rect.left, 0);

    if (visibleHeight <= 0 || visibleWidth <= 0) {
        return false;
    }

    const visibleArea = visibleHeight * visibleWidth;
    const elementArea = rect.height * rect.width;
    const visibleRatio = elementArea > 0 ? visibleArea / elementArea : 0;

    return visibleRatio >= threshold;
}

/**
 * 取消对元素的可见性观察
 * @param element 要取消观察的DOM元素
 */
export function unobserveElement(element: Element): void {
    const observer = elementObserverMap.get(element);
    if (observer) {
        observer.unobserve(element);
        observer.disconnect();
        elementObserverMap.delete(element);
    }
}

/**
 * 观察元素可见性变化
 * @param element 要观察的DOM元素
 * @param callback 可见性变化时的回调函数
 * @param options 配置选项
 * @returns 取消观察的函数
 */
export function observeElementVisibility(
    element: Element,
    callback: VisibilityCallback,
    options: VisibilityOptions = {}
): () => void {
    const {
        root = null,
        rootMargin = '0px',
        threshold = 0,
        once = false,
    } = options;

    // 优先使用 IntersectionObserver
    if ('IntersectionObserver' in window) {
        // 如果元素已经在观察中，先取消之前的观察
        if (elementObserverMap.has(element)) {
            unobserveElement(element);
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const isVisible = entry.isIntersecting &&
                    (entry.intersectionRatio >= (Array.isArray(threshold) ? threshold[0] : threshold));

                callback(isVisible, entry);

                // 如果是一次性观察，触发后立即取消观察
                if (once && isVisible) {
                    unobserveElement(element);
                }
            });
        }, {
            root,
            rootMargin,
            threshold: Array.isArray(threshold) ? threshold : [threshold],
        });

        observer.observe(element);
        elementObserverMap.set(element, observer);

        // 返回取消观察的函数
        return () => unobserveElement(element);
    }
    // 不支持 IntersectionObserver 的回退方案
    else {
        let isCurrentlyVisible = false;

        // 检查元素可见性的函数
        const checkVisibility = () => {
            const isVisible = isElementInViewport(element, Array.isArray(threshold) ? threshold[0] : threshold);

            if (isVisible !== isCurrentlyVisible) {
                isCurrentlyVisible = isVisible;
                callback(isVisible);

                // 如果是一次性观察且元素可见，移除事件监听
                if (once && isVisible) {
                    // eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
                    removeEventListeners();
                }
            }
        };

        // 添加事件监听
        const events = ['scroll', 'resize', 'load', 'orientationchange'];
        const handleEvent = throttle(checkVisibility, 100);

        events.forEach(event => {
            window.addEventListener(event, handleEvent);
        });

        // 初始检查
        checkVisibility();

        // 移除事件监听的函数
        const removeEventListeners = () => {
            events.forEach(event => {
                window.removeEventListener(event, handleEvent);
            });
        };

        // 返回取消观察的函数
        return removeEventListeners;
    }
}

/**
 * 一次性检测元素是否进入视口
 * @param element 要检测的DOM元素
 * @param callback 元素进入视口时的回调
 * @param options 配置选项
 * @returns 取消观察的函数
 */
export function onceElementVisible(
    element: Element,
    callback: () => void,
    options: Omit<VisibilityOptions, 'once'> = {}
): () => void {
    return observeElementVisibility(element, (isVisible) => {
        if (isVisible) {
            callback();
        }
    }, {
        ...options,
        once: true,
    });
}

/**
 * 按 builder id 维度保存随心搭状态到全局存储
 * @param builderId builder id
 * @param value 要保存的数据
 */
export function setFlexBuilderStateOfStore(
    builderId: string,
    value: Record<string, any>
) {
    const oldState = store.getFlexBuilderState();
    let currentBuilderState = oldState[builderId];
    if (!currentBuilderState) {
        currentBuilderState = {};
    }
    const newState = {
        ...oldState,
        [builderId]: {
            ...currentBuilderState,
            ...value,
        },
    };
    store.setFlexBuilderState(newState);
}
